import type { DialogueId, EncounterId } from "../content/types";
import {
  Bitmap,
  BitmapData,
  Color,
  ColorMatrix,
  ColorMatrixFilter,
  GlowFilter,
  MovieClip,
  Point,
  Rectangle,
  Sprite,
} from "../runtime";
import { EventStepAction, GuiAction } from "./actions";
import {
  blackBlock,
  G_hudMain,
  G_lifeBar,
  G_lifeBarBack,
  G_lifeOctagon,
  G_startMissionBar,
  G_w1,
  G_w1s,
  G_w2,
  G_w2s,
  G_w3,
  G_w3s,
  G_w4,
  G_w4s,
  G_w5,
  G_w5s,
  G_weaponBar,
  G_whiteFill,
  G_xenosAlpha,
  G_xenosBar,
  G_xenosBarBack,
  playerF1,
  playerF2,
  playerF3,
  playerF4,
  targetF1,
  targetF2,
  targetF3,
} from "./assets";
import type { DialogueResolver, EventState } from "./events";
import { resolveEvent } from "./events";
import { Label } from "./Label";
import { TextBox } from "./TextBox";

/** Marker on the minimap. `id` 0 is the player, 1 a target; 10 hides a cleared one. */
interface MiniMapItem {
  id: number;
  xx: number;
  yy: number;
}

/** Where the minimap sits on stage and how level pixels scale into it. */
interface MiniMapGeometry {
  readonly startX: number;
  readonly startY: number;
  readonly sizeX: number;
  readonly sizeY: number;
  /** level pixels -> minimap pixels */
  readonly pixel: number;
}

/** Sparse numeric lookup; a missing key reads as `undefined`. */
type NumTable = Readonly<Partial<Record<number, number>>>;

type BitmapClass = new () => BitmapData;

interface WeaponSlotArt {
  /** small icon sitting on the weapon bar */
  readonly bar: BitmapClass;
  /** large icon revealed while the slot is selected */
  readonly active: BitmapClass;
  /** slots 3-5 only appear once their weapon has been found */
  readonly needsPickup: boolean;
}

/** Weapon-bar art, indexed by weapon. */
const WEAPON_SLOT_ART: readonly WeaponSlotArt[] = [
  { bar: G_w1s, active: G_w1, needsPickup: false },
  { bar: G_w2s, active: G_w2, needsPickup: false },
  { bar: G_w3s, active: G_w3, needsPickup: true },
  { bar: G_w4s, active: G_w4, needsPickup: true },
  { bar: G_w5s, active: G_w5, needsPickup: true },
];
const WEAPON_SLOT_SPACING = 39;
const WEAPON_SLOT_HIT_X = 12;
const WEAPON_SLOT_HIT_WIDTH = 40;
const WEAPON_BAR_HEIGHT = 55;

/** Hue rotation of the xenos bar per sword level; levels not listed stay unrotated. */
const XENOS_BAR_HUE: NumTable = { 1: -65, 2: -136, 3: 163 };

/** The two halves of a mission bar and the off-screen side each slides in from. */
const MISSION_BAR_HALVES = [
  { x: -260, y: 240 + 8, rotation: 0 },
  { x: 260 + 720, y: 240 - 8, rotation: 180 },
] as const;

/** Mission-bar types that end the mission. */
const MISSION_BAR_COMPLETES: readonly number[] = [1, 2, 15];

/** `updateComplete` result, keyed by mission-bar type then by frames since the bar appeared. */
const MISSION_END_ACTIONS: Readonly<Partial<Record<number, NumTable>>> = {
  1: { 14: 2, 155: 3 },
  2: { 150: 1 },
  15: { 80: 1 },
};

/** Caption raised at frame 25 of a mission bar: the `Label` variant per mission-bar type. */
const MISSION_BAR_TEXT: NumTable = {
  0: 1,
  1: 3,
  2: 4,
  3: 5,
  4: 6,
  5: 7,
  6: 8,
  7: 9,
  8: 10,
  10: 20,
  15: 21,
  17: 22,
};

/** Mission-bar type whose caption counts survival waves instead of the level number. */
const SURVIVAL_WAVE_BAR = 17;

/** Frame at which each mission-bar type starts sliding back off screen. */
const MISSION_BAR_RETRACT: NumTable = {
  0: 90,
  1: 160,
  2: 130,
  3: 160,
  4: 130,
  5: 160,
  6: 160,
  7: 160,
  8: 160,
  10: 130,
  17: 130,
};

export class Hud extends MovieClip {
  highScoreLabel!: Label;
  itemFoundLabel!: Label;
  currentOptionLabel!: Label;
  readonly speakerTarget: number[] = [];
  movingBar: MovieClip[] = [];
  enemyAttacksOn = true;
  readonly mmItem: MiniMapItem[] = [];
  encounterTarget: EncounterId | null = null;
  action = GuiAction.None;
  soundAction = 0;
  lvlComplete = false;
  tBox: TextBox | null = null;
  eventObject: EventState | null = null;
  generatorsTotal = 0;
  survivalWave = 0;
  private lifeBar!: Bitmap;
  private xenosBar!: Bitmap;
  private numScore!: Label;
  private lifeOctagon!: MovieClip;
  private lifeOctagonFlashOn = false;
  private lifeOctagonFlashCounter = 0;
  private lifeBarFlash = 0;
  private weaponBar!: MovieClip;
  private readonly weaponSlotArray: Bitmap[] = [];
  private readonly weaponHitAreas: (Sprite | null)[] = [];
  private wGlowRising = false;
  private wLight = 0;
  private wlightOn = false;
  private weapon = 0;
  private wGlowCounter = 0;
  private xenosFlash = 0;
  private xenosFlashOn = false;
  private xenosLevel = 10;
  private movingBarState = 0;
  private movingBarCounter = 0;
  private missionBarType = 0;
  private readonly lvl: string;
  private readonly txt: Label[] = [];
  private introCounter = 0;
  private endCounter = 0;
  private readonly hudOffsetY = 4;
  // the player marker holds four frames but only ever cycles the first two, as in the original
  private readonly plaAnim = new MiniMapAnim(
    [
      new playerF1(0, 0),
      new playerF2(0, 0),
      new playerF3(0, 0),
      new playerF4(0, 0),
    ],
    2,
    3,
  );
  private readonly tarAnim = new MiniMapAnim(
    [new targetF1(0, 0), new targetF2(0, 0), new targetF3(0, 0)],
    3,
    6,
  );
  private mm!: MiniMapGeometry;
  private mmBitmap!: Bitmap;
  private mmBitmapData!: BitmapData;
  private mmRect!: Rectangle;
  private readonly miniMapSourceRect = new Rectangle(0, 0, 9, 9);
  private readonly miniMapDrawPos = new Point(0, 0);
  private readonly sceneType: string;
  private readonly hyperArmour: boolean;
  private eventCurrent: DialogueId = "";
  private eventPartCurrent = 0;
  private spacePressed = false;
  private whiteOn = false;
  private whiteAlpha = 0;
  private whiteFill: MovieClip | null = null;

  constructor(
    scene: string,
    level: string,
    weaponData: readonly number[],
    hyperArmour: boolean,
    private readonly resolveDialogue: DialogueResolver = () => undefined,
  ) {
    super();
    this.hyperArmour = hyperArmour;
    this.lvl = level;
    this.sceneType = scene;
    switch (this.sceneType) {
      case "level":
        this.setupLevelHud(weaponData);
        break;
      case "level select":
        this.setupLevelSelectHud();
        break;
    }
  }

  update(input: readonly boolean[]): void {
    if (this.xenosLevel === 3 && !this.xenosFlashOn) {
      this.xenosFlashOn = true;
      this.xenosFlash = 1;
    }
    if (this.xenosLevel < 3 && this.xenosFlashOn) {
      this.xenosFlashOn = false;
      this.xenosBar.transform.colorTransform = tint(16777215, 0);
    }
    if (this.xenosFlashOn) {
      ++this.xenosFlash;
      if (this.xenosFlash === 2) {
        this.xenosBar.transform.colorTransform = tint(16777215, 1);
      }
      if (this.xenosFlash === 4) {
        this.xenosBar.transform.colorTransform = tint(16777215, 0);
        this.xenosFlash = 0;
      }
    }
    if (this.introCounter < 500) {
      ++this.introCounter;
      if (this.sceneType === "level" && this.introCounter === 5) {
        this.setupMissionBar(0);
      }
    }
    if (this.movingBar.length > 0) {
      this.updateMovingBar();
    }
    if (this.txt.length > 0) {
      this.updateTxt();
    }
    this.updateEvents(input);
    if (this.sceneType === "level") {
      this.updateMiniMap();
      this.updateLifeBar();
    }
    this.updateWhiteFill();
  }

  updateWeaponSlot(
    energy: number,
    specialCost: number,
    swordLevel: number,
    swordColour: number,
  ): void {
    if (this.wGlowRising) {
      ++this.wGlowCounter;
      if (this.wGlowCounter >= 18) {
        this.wGlowRising = false;
      }
    } else {
      --this.wGlowCounter;
      if (this.wGlowCounter <= 10) {
        this.wGlowRising = true;
      }
    }
    const slot: Bitmap | undefined = this.weaponSlotArray[this.weapon];
    if (slot == null) {
      return;
    }
    if (this.wLight > 0) {
      this.wLight -= 5;
      slot.transform.colorTransform = tint(16777215, this.wLight * 0.01);
    }
    if (energy >= specialCost) {
      if (!this.wlightOn) {
        this.wlightOn = true;
        this.wLight = 150;
      }
      const glowMult = (swordLevel + 1) * 0.3;
      const glowStrength = glowMult + 1.75;
      const glowSize = this.wGlowCounter * glowMult + 2;
      const outer = new GlowFilter(
        swordColour,
        1,
        glowSize,
        glowSize,
        glowStrength,
        1,
      );
      const inner = new GlowFilter(
        16777215,
        1,
        glowSize,
        glowSize,
        glowStrength,
        1,
        true,
      );
      slot.filters = [inner, outer];
    } else if (this.wlightOn) {
      this.wlightOn = false;
      slot.filters = [];
    }
  }

  newEvent(eventId: DialogueId, nEvent = true, extraValue = 0): void {
    // entering a step retires the line before it; the box is dropped rather
    // than played out so nothing lingers over the one opening in its place
    if (this.tBox != null) {
      this.removeChild(this.tBox);
      this.tBox = null;
    }
    if (nEvent) {
      this.eventCurrent = eventId;
      this.eventPartCurrent = 0;
    }
    const step = resolveEvent(
      this.resolveDialogue,
      this.eventCurrent,
      this.eventPartCurrent,
      {
        generators: this.generatorsTotal,
        score: extraValue,
      },
    );
    this.eventObject = step;
    this.speakerTarget.push(step.speaker);
    if (step.action !== EventStepAction.NoText && step.hasTextBox) {
      if (this.hyperArmour && step.speaker === 0) {
        step.pose = 1;
      }
      this.tBox = new TextBox(step.txt, step.speaker, step.pose);
      this.addChild(this.tBox);
      this.soundAction = 1;
    }
    this.encounterTarget = step.encounter ?? null;
    this.runEventAction(step.action);
    ++this.eventPartCurrent;
  }

  setupMiniMap(levelWidth = 0, levelHeight = 0): void {
    let longestDimension = levelWidth;
    if (levelHeight > levelWidth) {
      longestDimension = levelHeight;
    }
    const blockLength = 60 / longestDimension;
    const mapXLength = blockLength * levelWidth;
    const mapYLength = blockLength * levelHeight;
    const mapXStart = 642 - mapXLength * 0.5;
    const mapYStart = 40 - mapYLength * 0.5 + this.hudOffsetY;
    this.mm = {
      startX: mapXStart,
      startY: mapYStart,
      sizeX: mapXLength,
      sizeY: mapYLength,
      pixel: 60 / (longestDimension * 512),
    };
    const miniMapBackground: Sprite = new Sprite();
    miniMapBackground.graphics.beginBitmapFill(new blackBlock());
    miniMapBackground.graphics.drawRect(
      mapXStart,
      mapYStart,
      mapXLength,
      mapYLength,
    );
    this.addChild(miniMapBackground);
    this.addMiniMapItem(0, 0, 0);
    this.mmRect = new Rectangle(0, 0, mapXLength, mapYLength);
    this.mmBitmapData = new BitmapData(mapXLength, mapYLength, true, 13816575);
    this.mmBitmap = new Bitmap(this.mmBitmapData);
    this.mmBitmap.x = mapXStart;
    this.mmBitmap.y = mapYStart;
    this.addChild(this.mmBitmap);
  }

  addMiniMapItem(id = 0, x = 0, y = 0): void {
    this.mmItem.push({ id, xx: x, yy: y });
  }

  setupMissionBar(mode = 0): void {
    this.missionBarType = mode;
    this.movingBarState = 0;
    this.movingBarCounter = 0;
    for (const half of MISSION_BAR_HALVES) {
      const mbHolder = new MovieClip();
      mbHolder.x = half.x;
      mbHolder.y = half.y;
      mbHolder.rotation = half.rotation;
      this.addChild(mbHolder);
      const mbBit = new Bitmap(new G_startMissionBar());
      mbBit.x = mbBit.width * -0.5;
      mbBit.y = mbBit.height * -0.5;
      mbHolder.addChild(mbBit);
      this.movingBar.push(mbHolder);
    }
    if (MISSION_BAR_COMPLETES.includes(this.missionBarType)) {
      this.lvlComplete = true;
    }
  }

  updateComplete(): number {
    ++this.endCounter;
    return MISSION_END_ACTIONS[this.missionBarType]?.[this.endCounter] ?? 0;
  }

  newHighScore(): void {
    const txtNewHighScore: Label = new Label(23, 0, 165);
    this.addChild(txtNewHighScore);
    this.txt.push(txtNewHighScore);
  }

  updateScore(score = 0): void {
    this.numScore.updateText(String(score));
  }

  updateHP(hp = 100): void {
    this.lifeBar.scaleX = hp * 0.01;
    const oScale = hp * 0.0075 + 0.25;
    this.lifeOctagon.scaleX = oScale;
    this.lifeOctagon.scaleY = oScale;
  }

  damage(): void {
    this.lifeBarFlash = 140;
  }

  updateWeapon(newWeapon = 0): void {
    const c = tint(16777215, 0);
    for (
      let weaponIndex = 0;
      weaponIndex < this.weaponSlotArray.length;
      weaponIndex++
    ) {
      const slot = this.weaponSlotArray[weaponIndex];
      slot.filters = [];
      slot.visible = weaponIndex === newWeapon;
      slot.transform.colorTransform = c;
    }
    // the original only latched a weapon that owns a slot
    if (newWeapon >= 0 && newWeapon < this.weaponSlotArray.length) {
      this.weapon = newWeapon;
    }
    this.wlightOn = false;
    this.wLight = 0;
  }
  /** Returns the owned weapon slot under a stage-space pointer. */
  weaponAt(stageX: number, stageY: number): number | null {
    for (
      let weaponIndex = 0;
      weaponIndex < this.weaponHitAreas.length;
      weaponIndex++
    ) {
      if (this.weaponHitAreas[weaponIndex]?.hitTestPoint(stageX, stageY)) {
        return weaponIndex;
      }
    }
    return null;
  }

  updateXenos(xenos = 0, newLevel = 0): void {
    if (this.xenosLevel !== newLevel) {
      this.xenosLevel = newLevel;
      const cm = new ColorMatrix();
      cm.adjustColor(0, 0, 0, XENOS_BAR_HUE[this.xenosLevel] ?? 0);
      this.xenosBar.filters = [new ColorMatrixFilter(cm)];
    }
    this.xenosBar.scaleX = xenos * 0.000333;
  }

  /** Side effects an event step can request, keyed by its `action` code. */
  private runEventAction(action: EventStepAction): void {
    switch (action) {
      case EventStepAction.EnableEnemyAttacks:
        this.enemyAttacksOn = true;
        break;
      case EventStepAction.DisableEnemyAttacks:
        this.enemyAttacksOn = false;
        break;
      case EventStepAction.WhiteoutOn:
        this.whiteOn = true;
        this.action = GuiAction.GargantuanExplosion;
        break;
      case EventStepAction.WhiteoutOff:
        this.whiteOn = false;
        break;
      case EventStepAction.Win:
        this.action = GuiAction.Win;
        break;
      case EventStepAction.BossVanish:
        this.action = GuiAction.BossVanishAndAwardWeapon;
        break;
      case EventStepAction.BossStop:
        this.action = GuiAction.BossStop;
        break;
      case EventStepAction.BossResume:
        this.action = GuiAction.BossResume;
        break;
      case EventStepAction.ActivateEncounter:
        this.action = GuiAction.ActivateEncounter;
        break;
      case EventStepAction.CameraShake:
        this.action = GuiAction.CameraShake;
        break;
      case EventStepAction.ShowEnergyCrusherUnlock:
        this.setupMissionBar(5);
        break;
      case EventStepAction.ShowGravitySlicerUnlock:
        this.setupMissionBar(6);
        break;
      case EventStepAction.ShowMegatronUnlock:
        this.setupMissionBar(7);
        break;
      case EventStepAction.ShowBarrierWingUnlock:
        this.setupMissionBar(8);
        this.action = GuiAction.UpgradeArmourPreview;
        break;
      case EventStepAction.StartCleanser:
        this.action = GuiAction.StartCleanser;
        break;
      case EventStepAction.FinalShardBurst:
        this.action = GuiAction.FinalShardBurst;
        break;
      case EventStepAction.BossFlashAndResume:
        this.action = GuiAction.BossFlashAndResume;
        break;
      case EventStepAction.StartBossFight:
        this.setupMissionBar(10);
        this.action = GuiAction.StartBossMusic;
        break;
      case EventStepAction.ReturnToMainMenu:
        this.action = GuiAction.ReturnToMainMenu;
        break;
    }
  }

  /** In-mission HUD: life bar, xenos meter, weapon bar and score, in draw order. */
  private setupLevelHud(weaponData: readonly number[]): void {
    const hudMainBit = new Bitmap(new G_hudMain());
    hudMainBit.x = 0;
    hudMainBit.y = this.hudOffsetY;
    hudMainBit.alpha = 0.75;
    hudMainBit.cacheAsBitmap = true;
    const lifeBarBack = new Bitmap(new G_lifeBarBack());
    lifeBarBack.x = 87;
    lifeBarBack.y = 40;
    lifeBarBack.cacheAsBitmap = true;
    this.addChild(lifeBarBack);
    this.lifeBar = new Bitmap(new G_lifeBar());
    this.lifeBar.x = 87;
    this.lifeBar.y = 40;
    this.addChild(this.lifeBar);
    this.lifeOctagon = new MovieClip();
    this.lifeOctagon.x = 53;
    this.lifeOctagon.y = 44;
    this.addChild(this.lifeOctagon);
    const octagonBit = new Bitmap(new G_lifeOctagon());
    octagonBit.x = octagonBit.width * -0.5;
    octagonBit.y = octagonBit.height * -0.5;
    this.lifeOctagon.addChild(octagonBit);
    const xenosBarBack = new Bitmap(new G_xenosBarBack());
    xenosBarBack.x = 360 - xenosBarBack.width * 0.5;
    xenosBarBack.y = 24;
    xenosBarBack.cacheAsBitmap = true;
    this.addChild(xenosBarBack);
    this.xenosBar = new Bitmap(new G_xenosBar());
    this.xenosBar.x = xenosBarBack.x;
    this.xenosBar.y = xenosBarBack.y;
    this.addChild(this.xenosBar);
    const xenosBarMask = new Bitmap(new G_xenosAlpha());
    xenosBarMask.x = xenosBarBack.x;
    xenosBarMask.y = xenosBarBack.y;
    this.addChild(xenosBarMask);
    this.xenosBar.cacheAsBitmap = true;
    xenosBarMask.cacheAsBitmap = true;
    this.xenosBar.mask = xenosBarMask;
    const weaponBarSpr = new Bitmap(new G_weaponBar());
    weaponBarSpr.cacheAsBitmap = true;
    this.weaponBar = new MovieClip();
    this.weaponBar.x = weaponBarSpr.width * -0.5 + 360;
    this.weaponBar.y = 405;
    this.addChild(this.weaponBar);
    this.weaponBar.addChild(weaponBarSpr);
    this.addWeaponBarIcons(weaponData);
    this.addWeaponSlotHighlights();
    this.addChild(hudMainBit);
    this.numScore = new Label(0, 473, 28);
    this.addChild(this.numScore);
    this.updateScore(0);
    this.updateXenos(0);
  }

  /** Mission-select HUD: option label, high score and item counter. */
  private setupLevelSelectHud(): void {
    this.currentOptionLabel = new Label(13, 340, 3);
    this.addChild(this.currentOptionLabel);
    const txtHighScore = new Label(11, 440, 441);
    this.addChild(txtHighScore);
    this.highScoreLabel = new Label(12, 440, 441);
    this.addChild(this.highScoreLabel);
    this.itemFoundLabel = new Label(15, 40, 441);
    this.addChild(this.itemFoundLabel);
  }

  /** Small icons sitting on the weapon bar, one per weapon the player owns. */
  private addWeaponBarIcons(weaponData: readonly number[]): void {
    for (
      let weaponIndex = 0;
      weaponIndex < WEAPON_SLOT_ART.length;
      weaponIndex++
    ) {
      const art = WEAPON_SLOT_ART[weaponIndex];
      if (art.needsPickup && weaponData[weaponIndex] < 2) {
        continue;
      }
      const icon = new Bitmap(new art.bar());
      icon.x = 15 + weaponIndex * WEAPON_SLOT_SPACING;
      icon.y = 10;
      this.weaponBar.addChild(icon);
      const hitArea = new Sprite();
      hitArea.graphics.drawRect(
        WEAPON_SLOT_HIT_X + weaponIndex * WEAPON_SLOT_SPACING,
        0,
        WEAPON_SLOT_HIT_WIDTH,
        WEAPON_BAR_HEIGHT,
      );
      this.weaponBar.addChild(hitArea);
      this.weaponHitAreas[weaponIndex] = hitArea;
    }
  }

  /** Large per-weapon icons, hidden until `updateWeapon` selects one. */
  private addWeaponSlotHighlights(): void {
    for (
      let weaponIndex = 0;
      weaponIndex < WEAPON_SLOT_ART.length;
      weaponIndex++
    ) {
      const highlight = new Bitmap(new WEAPON_SLOT_ART[weaponIndex].active());
      highlight.x =
        highlight.width * -0.5 + 32 + weaponIndex * WEAPON_SLOT_SPACING;
      highlight.y = highlight.height * -0.5 + 27;
      highlight.visible = false;
      this.weaponBar.addChild(highlight);
      this.weaponSlotArray.push(highlight);
    }
  }

  private updateEvents(input: readonly boolean[]): void {
    if (input[4]) {
      if (!this.spacePressed) {
        this.spacePressed = true;
        // a press retires the line on screen at once and opens the next step on
        // the same frame, so a conversation is clicked through as fast as the
        // player can tap
        if (this.eventObject != null && this.eventObject.hasTextBox) {
          this.newEvent(this.eventCurrent, false);
        }
      }
    } else if (this.spacePressed) {
      this.spacePressed = false;
    }
    // textless steps are scripted beats, so they still run on authored timing
    if (this.eventObject != null && !this.eventObject.ready) {
      ++this.eventObject.counter;
      if (this.eventObject.counter === this.eventObject.readyTime) {
        if (this.eventObject.hasTextBox) {
          this.eventObject.ready = true;
          this.eventObject.counter = 0;
        } else if (this.eventObject.action !== EventStepAction.NoText) {
          this.newEvent(this.eventCurrent, false);
        } else {
          this.eventObject = null;
        }
      }
    }
    this.tBox?.update();
  }

  private updateMiniMap(): void {
    this.tarAnim.advance();
    this.plaAnim.advance();
    this.mmBitmapData.lock();
    this.mmBitmapData.fillRect(this.mmRect, 16777215);
    const tileRect = this.miniMapSourceRect;
    for (const item of this.mmItem) {
      if (item.id >= 10) {
        continue;
      }
      const drawPos = this.miniMapDrawPos;
      drawPos.x = item.xx * this.mm.pixel - 4;
      drawPos.y = item.yy * this.mm.pixel - 4;
      const marker = item.id === 0 ? this.plaAnim.bitmap : this.tarAnim.bitmap;
      this.mmBitmapData.copyPixels(marker, tileRect, drawPos, null, null, true);
    }
    this.mmBitmapData.unlock();
  }

  /** Damage flash on the life bar and the low-health pulse on its octagon. */
  private updateLifeBar(): void {
    if (this.lifeBarFlash > 0) {
      this.lifeBarFlash -= 20;
      this.lifeBar.transform.colorTransform = tint(
        16711680,
        this.lifeBarFlash * 0.01,
      );
    }
    if (this.lifeOctagonFlashOn) {
      ++this.lifeOctagonFlashCounter;
      if (
        this.lifeOctagonFlashCounter === 2 ||
        this.lifeOctagonFlashCounter === 4
      ) {
        this.lifeOctagon.transform.colorTransform = tint(
          16777215,
          this.lifeOctagonFlashCounter === 2 ? 1 : 0,
        );
      }
    }
    if (this.lifeBar.scaleX <= 0.2) {
      this.lifeOctagonFlashOn = true;
    } else if (this.lifeOctagonFlashOn) {
      this.lifeOctagonFlashOn = false;
      this.lifeOctagonFlashCounter = 0;
      this.lifeOctagon.transform.colorTransform = tint(16777215, 0);
    }
  }

  /** Full-screen white flash an event step can raise and drop. */
  private updateWhiteFill(): void {
    if (this.whiteOn) {
      if (this.whiteFill == null) {
        this.whiteFill = new MovieClip();
        this.addChild(this.whiteFill);
        this.whiteFill.addChild(new Bitmap(new G_whiteFill()));
      }
      if (this.whiteAlpha < 100) {
        this.whiteAlpha += 2;
        this.whiteFill.alpha = this.whiteAlpha * 0.01;
      }
    } else if (this.whiteAlpha > 0) {
      --this.whiteAlpha;
      this.whiteFill!.alpha = this.whiteAlpha * 0.01;
      if (this.whiteAlpha === 0) {
        this.removeChild(this.whiteFill!);
        this.whiteFill = null;
      }
    }
  }

  private updateTxt(): void {
    for (let textIndex = 0; textIndex < this.txt.length; textIndex++) {
      if ((this.txt[textIndex].update() | 0) === 1) {
        this.removeChild(this.txt[textIndex]);
        this.txt.splice(textIndex, 1);
        textIndex--;
      }
    }
  }

  private updateMovingBar(): void {
    ++this.movingBarCounter;
    if (this.movingBarCounter === 10 && this.missionBarType !== 2) {
      this.action = GuiAction.MissionBarIn;
    }
    if (this.movingBarCounter === 25) {
      const caption = MISSION_BAR_TEXT[this.missionBarType];
      if (caption !== undefined) {
        const extra =
          this.missionBarType === SURVIVAL_WAVE_BAR
            ? this.survivalWave
            : this.lvl;
        const captionTxt = new Label(caption, 160, 218, extra);
        this.addChild(captionTxt);
        this.txt.push(captionTxt);
      }
    }
    if (this.movingBarCounter === MISSION_BAR_RETRACT[this.missionBarType]) {
      this.movingBarState = 1;
      if (this.missionBarType === 0) {
        this.action = GuiAction.MissionBarOut;
      }
    }
    if (this.missionBarType === 0 && this.movingBarCounter === 95) {
      const goTxt = new Label(2, 160, 210);
      this.addChild(goTxt);
      this.txt.push(goTxt);
      this.action = GuiAction.MissionStarted;
    }
    for (let barIndex = 0; barIndex < this.movingBar.length; barIndex++) {
      const bar = this.movingBar[barIndex];
      if (this.movingBarState === 0) {
        // both can fire in one tick when the bar overshoots the centre
        if (bar.x < 360) {
          bar.x += 20;
        }
        if (bar.x > 360) {
          bar.x -= 20;
        }
      }
      if (this.movingBarState === 1) {
        if (barIndex === 0) {
          if (bar.y < 480 + 100) {
            bar.y += 20;
          }
        } else if (bar.y > -100) {
          bar.y -= 20;
          if (bar.y <= -100) {
            this.removeChild(this.movingBar[0]);
            this.removeChild(this.movingBar[1]);
            this.movingBar = [];
          }
        }
      }
    }
  }
}

/** Looping frame cycle for one minimap marker. */
class MiniMapAnim {
  private frame = 0;
  private counter = 0;

  constructor(
    private readonly frames: readonly BitmapData[],
    private readonly limit: number,
    private readonly speed: number,
  ) {}

  /** The frame to blit this tick. */
  get bitmap(): BitmapData {
    return this.frames[this.frame];
  }

  advance(): void {
    ++this.counter;
    if (this.counter === this.speed) {
      this.counter = 0;
      ++this.frame;
      if (this.frame === this.limit) {
        this.frame = 0;
      }
    }
  }
}

/** `fl.motion.Color` tint as a one-liner. */
function tint(colour: number, amount: number): Color {
  const c = new Color();
  c.setTint(colour, amount);
  return c;
}
